import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SelectItem } from 'primeng/api';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { filter, map, tap } from 'rxjs/operators';
import { AuthUiHelperService } from 'src/app/auth/auth.ui.helper.service';
import { GetTenantGQL, Tenant } from 'src/generated/graphql';
import { ENABLED_LANGUAGES, ENABLED_LANGUAGES_ADMIN_INTERFACE } from '../constans';

/**
 * The NebulrTranslationService helps UI to render different languages / translations. It wraps ngx-translate under the hood.
 * On initialization, it will query GetTenantGQL and set current language based on Tenant locale.
 * An anonymous clone `NebulrAnonymousTranslationService` (that queries GetTenantAnonymousGQL instead) is available.
 */
@Injectable({
  providedIn: 'root',
})
export class NebulrTranslationService {

  /**
   * Convenient way for displaying languages in a translated dropdown. Will be re translated on language change, keeping reference to the array.
   */
  public selectableLanguages: SelectItem[];
  public selectableLanguagesOriginalLang: SelectItem[];

  public languageChanged: BehaviorSubject<string>;

  private loadedLanguages: Record<string, Record<string, unknown>> = {};

  constructor(
    private readonly httpClient: HttpClient,
    private readonly translateService: TranslateService,
    private readonly getTenantGQL: GetTenantGQL,
    private readonly authUiHelperService: AuthUiHelperService
    ) {

    // Register the default lang as the lang currently in use!
    this.languageChanged = new BehaviorSubject(this.translateService.currentLang);
    this.translateService.use(this.translateService.defaultLang);

    // Download all enabled languages as JSONS via HTTP. If we had race condition with getTenantGQL, we will retry to setLanguage() again after download

    const authenticatedUser$ = this.authUiHelperService.currentUser$.pipe(filter((u) => u.authenticated));
    const langDownloads$ = ENABLED_LANGUAGES.map(lang => this.httpClient.get<Record<string, unknown>>(`./assets/i18n/${lang.key}.json`).pipe(tap((r) => r['LANG_ID'] = lang.key)));
    const currentTenantWatch$:Observable<Tenant> = this.getTenantGQL.watch({}).valueChanges.pipe(map((results) => results.data.getTenant));

    // Make sure user is authenticated before trying to pull tenant
    //TODO with this approach we're not downloading SV until someone is authenticated!
    authenticatedUser$.subscribe(() => {
      combineLatest([currentTenantWatch$, ...langDownloads$]).subscribe(([t, ...langs]) => {
        const tenant: Tenant = t as Tenant;
        langs.forEach((lang) => {
          this.loadedLanguages[lang['LANG_ID'] as string] = lang;
        });
        if (this.translateService.currentLang !== tenant.locale) {
          this._setLanguage(tenant.locale);
        }
      });
    });

    // // Construct a translated easy to use array of language options for UI display. Retranslate it if current language is changed
    this.selectableLanguages = this._constructSelectableLanguages();
    this.selectableLanguagesOriginalLang = this._constructSelectableLanguagesOriginalLang();

    this.translateService.onLangChange.subscribe(() => {
      this._constructSelectableLanguages().forEach((newItem) => {
        const match = this.selectableLanguages.find(currentItem => currentItem.value === newItem.value);
        match.label = newItem.label;
      });
      this.languageChanged.next(this.translateService.currentLang);
    });
  }

  getAdminLanguages(): SelectItem[] {
    return this.selectableLanguagesOriginalLang.filter(
      (lang: SelectItem) => ENABLED_LANGUAGES_ADMIN_INTERFACE.includes(lang.value)
    );
  }

  /**
   * Sets the current language via ngx-translate.
   * @param language 
   * @returns 
   */
   setLanguage(language: string): Promise<void> {
    try {
      this._setLanguage(language)
      return null;
    } catch (error) {
      console.log(error);
      //throw error;
    }
  }

  /**
   * Sets the current language via ngx-translate.
   * @param language 
   * @returns 
   */
  _setLanguage(language: string): Promise<void> {
    if (!this.translateService.getLangs().includes(language))
      this.translateService.setTranslation(language, this._getTranslations(language), true);
    this.translateService.use(language).subscribe();
    return null;
  }

  /**
   * Convenient way for displaying languages in a translated dropdown
   * @returns 
   */
  private _constructSelectableLanguages(): SelectItem[] {
    return ENABLED_LANGUAGES.map(lang => {return {value: lang.key, label: this.translateService.instant(`LANGUAGES.${lang.key.toUpperCase()}`)}});
  }

  private _constructSelectableLanguagesOriginalLang(): SelectItem[] {
    return ENABLED_LANGUAGES.map(lang => {return {value: lang.key, label: lang.label}});
  }

  private _getTranslations(language: string): Record<string, unknown> {
    if (ENABLED_LANGUAGES.find(lang => lang.key === language) && !{}.hasOwnProperty.call(this.loadedLanguages, language))
      throw new LanguageNotYetLoadedError(`${language} not loaded yet`);

    return this.loadedLanguages[language];
  }
}

export class LanguageNotYetLoadedError extends Error {
  constructor(msg: string) {
    super(msg);
  }
}
